'use client';

import Link from 'next/link';
import { Globe, ArrowLeft, MapPin, Clock, Phone, Mail, ExternalLink } from 'lucide-react';

import { LocalBusinessStructuredData, BreadcrumbStructuredData } from '@/components/directory/StructuredData';
import RelatedStores from '@/components/directory/RelatedStores';
import StoreRatingsSection from '@/components/directory/StoreRatingsSection';
import GoogleMapEmbed from '@/components/shared/GoogleMapEmbed';
import StoreViewTracker from '@/components/tracking/StoreViewTracker';
import BusinessHoursCollapsible from '@/components/storefront/BusinessHoursCollapsible';
import ContactInformationCollapsible from '@/components/directory/ContactInformationCollapsible';
import WhatsAppCtaButton from '@/components/directory/WhatsAppCtaButton';
import DirectoryPhotoGalleryDisplay from '@/components/directory/DirectoryPhotoGalleryDisplay';
import { showsListingPhotoGallery } from '@/lib/place-photo-gallery';
import ProductCategoriesCollapsible from '@/components/directory/ProductCategoriesCollapsible';
import SmartProductCard from '@/components/products/SmartProductCard';
import EnhancedProductDisplay from '@/components/storefront/EnhancedProductDisplay';
import { TenantPaymentProvider } from '@/contexts/TenantPaymentContext';
import DirectoryKeywordTags from '@/components/directory/DirectoryKeywordTags';
import { StorefrontStatusPanel } from '@/components/storefront/StorefrontStatusPanel';
import { TenantQRCode } from '@/components/public/TenantQRCode';
import { ClaimedReportCta } from '@/components/directory/ClaimedReportCta';
import FaqStorefrontDisplay from '@/components/faq/FaqStorefrontDisplay';
import PublicInquiryForm from '@/components/crm/PublicInquiryForm';
import LastViewed from '@/components/directory/LastViewed';
import { PoweredByFooter } from '@/components/PoweredByFooter';
import CouponSpotlight from '@/components/storefront/CouponSpotlight';
import FlyerSection from '@/components/storefront/FlyerSection';
import HoursStatusBadge from '@/components/storefront/HoursStatusBadge';
import DemoBadge from '@/components/shared/DemoBadge';

import type { DirectoryEntryLayoutProps } from './types';
import { useQrScanTracking } from '@/hooks/useQrScanTracking';
import { useDirectoryPresenceTracking } from '@/hooks/useDirectoryPresenceTracking';

export default function DirectoryEntryEditorialLayout(props: DirectoryEntryLayoutProps) {
  const {
    tenantId, listing, tenantLogo, businessProfile, businessHours,
    storefrontCategories, featuredProducts, activeFeatured, tenantInfo, slugForRelated,
    showStatusPanel, hoursStatus, showsHours,
    showsMap, showsLocation, currentUrl, baseUrl, faqFlags, crmFlags,
    paymentGatewayStatus, actualProductCount, fullAddress,
    isDemo, demoExpiresAt, directoryEntryOptions, isStorefrontEnabled,
  } = props;

  const canShowLogo = directoryEntryOptions?.logoEnabled ?? directoryEntryOptions?.canShowLogo ?? true;
  const canShowAbout = directoryEntryOptions?.aboutEnabled ?? directoryEntryOptions?.canShowAbout ?? true;
  const canShowGallery = showsListingPhotoGallery(listing, directoryEntryOptions);
  const canShowQr = directoryEntryOptions?.qrEnabled ?? true;
  const canShowContact = directoryEntryOptions?.contactEnabled ?? true;
  const whatsappCtaNumber = (directoryEntryOptions?.canShowWhatsapp ?? false)
    ? (directoryEntryOptions?.whatsappCtaNumber ?? null)
    : null;

  // Track QR code scans when visitor arrives via QR code
  useQrScanTracking(tenantId, 'directory');

  // Layer 3 — engagement events for the claimed entry surface.
  const { trackCallClick, trackStorefrontClick } = useDirectoryPresenceTracking({
    slug: listing?.slug || slugForRelated || '',
    active: !!listing?.slug,
  });

  const primaryColor = tenantInfo?.metadata?.primaryColor || tenantInfo?.metadata?.primary_color || null;

  return (
    <>
      <LocalBusinessStructuredData
        listing={{
          ...listing,
          description: listing.description,
          sameAs: listing.sameAs,
          schemaType: listing.schemaTypeHint,
        }}
        url={currentUrl}
      />
      <BreadcrumbStructuredData items={[
        { name: 'Home', url: baseUrl },
        { name: 'Directory', url: `${baseUrl}/directory` },
        { name: listing.businessName, url: currentUrl },
      ]} />
      <StoreViewTracker tenantId={tenantId} storeName={listing.businessName} categories={listing.categories} listingOrigin="claimed" surface="directory" />

      <div className="min-h-screen bg-white">
        {/* Editorial Hero */}
        <div
          className="relative text-white overflow-hidden"
          style={primaryColor ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}88 50%, ${primaryColor}33 100%)` } : { backgroundColor: '#171717' }}
        >
          <div className="absolute inset-0 opacity-20">
            {listing.coverImageUrl ? (
              <img src={listing.coverImageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900" />
            )}
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-16 sm:py-24">
            <Link href="/directory" className="inline-flex items-center text-sm text-neutral-300 hover:text-white mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Directory
            </Link>
            {showStatusPanel && tenantInfo ? (
              <StorefrontStatusPanel tenantInfo={tenantInfo} />
            ) : (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  {listing.logoUrl && canShowLogo && (
                    <img src={listing.logoUrl} alt={listing.businessName} className="w-20 h-20 rounded-xl object-cover border-2 border-white/20" />
                  )}
                  <div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight flex items-center gap-2 flex-wrap">{listing.businessName}<DemoBadge isDemo={isDemo} demoExpiresAt={demoExpiresAt} size="md" /></h1>
                    {listing.categories && listing.categories.length > 0 && (
                      <p className="text-neutral-300 mt-2 text-lg">
                        {listing.categories.filter((c: any) => c.isPrimary).map((c: any) => c.name).join(', ') || listing.categories[0]?.name}
                      </p>
                    )}
                  </div>
                </div>
                {isStorefrontEnabled && (
                  <p className="text-neutral-300 text-lg max-w-2xl leading-relaxed">
                    Browse {actualProductCount > 0 ? actualProductCount : (listing.productCount ?? 0)} products and shop directly from their online storefront.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4">
                  {isStorefrontEnabled && (
                    <Link href={`/tenant/${slugForRelated || listing.tenantId}`}
                      onClick={trackStorefrontClick}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white text-neutral-900 rounded-lg hover:bg-neutral-100 transition-colors font-semibold">
                      <Globe className="w-5 h-5" /> Visit Storefront
                    </Link>
                  )}
                  {showsHours && (
                    <HoursStatusBadge status={hoursStatus} size="lg" animate={true} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Coupon Spotlight Banner */}
        <section className="max-w-6xl mx-auto px-6 -mt-6 relative z-20 mb-8">
          <CouponSpotlight tenantId={listing.tenantId} variant="banner" />
        </section>

        <FlyerSection tenantSlug={listing.tenantId} tenantId={listing.tenantId} surface="directory" />

        {/* Asymmetric Content Grid */}
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Column */}
            <div className="lg:col-span-8 space-y-10">
              {/* About */}
              {canShowAbout && (listing.description || businessProfile?.business_description || businessProfile?.businessDescription) && (
                <section>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">About</h2>
                  {listing.description && (
                    <p className="text-neutral-700 leading-relaxed text-lg mb-3">
                      {listing.description}
                    </p>
                  )}
                  {(businessProfile?.business_description || businessProfile?.businessDescription) && (
                    <p className="text-neutral-600 leading-relaxed text-lg whitespace-pre-wrap">
                      {businessProfile.business_description || businessProfile.businessDescription}
                    </p>
                  )}
                </section>
              )}

              {/* Gallery */}
              {!showStatusPanel && canShowGallery && (
                <DirectoryPhotoGalleryDisplay listing={listing} {...businessProfile} isPublished={true} />
              )}

              {/* Active Featured Products (from ActiveFeaturedResolver) */}
              {!showStatusPanel && activeFeatured?.hasActive && activeFeatured.products.length > 0 && (
                <TenantPaymentProvider tenantId={listing.tenantId}>
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-neutral-900 dark:text-white mb-3">Featured</h2>
                    <EnhancedProductDisplay
                      products={activeFeatured.products as any}
                      tenantId={listing.tenantId}
                      displayMode="carousel"
                      carouselItemsVisible={4}
                      variant="grid"
                    />
                  </div>
                </TenantPaymentProvider>
              )}

              {/* Featured Products */}
              {!showStatusPanel && featuredProducts.length > 0 && (
                <TenantPaymentProvider tenantId={listing.tenantId}>
                  <section>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-neutral-900">Curated Selection</h2>
                      <Link href={`/tenant/${slugForRelated || listing.tenantId}`} className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All →</Link>
                    </div>
                    <div className="space-y-6">
                      {(() => {
                        const productTypeOrder = ['physical', 'digital', 'service', 'hybrid'] as const;
                        const productTypeLabels: Record<string, string> = {
                          physical: 'Physical Products',
                          digital: 'Digital Products',
                          service: 'Service Products',
                          hybrid: 'Hybrid Products',
                        };
                        const grouped = productTypeOrder.reduce((acc, pt) => {
                          acc[pt] = featuredProducts.filter((p: any) => (p.productType || 'physical') === pt);
                          return acc;
                        }, {} as Record<string, any[]>);
                        const otherProducts = featuredProducts.filter((p: any) => {
                          const pt = p.productType || 'physical';
                          return !productTypeOrder.includes(pt as any);
                        });

                        return (
                          <>
                            {productTypeOrder.map(pt => {
                              const group = grouped[pt];
                              if (!group || group.length === 0) return null;
                              return (
                                <div key={pt}>
                                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">{productTypeLabels[pt]}</h3>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {group.map((product: any) => (
                                      <SmartProductCard key={`editorial-${product.id}`} tenantId={listing.tenantId}
                                        product={{
                                          id: product.id, sku: product.sku || product.id,
                                          name: product.name || product.title, title: product.title || product.name,
                                          brand: product.brand, description: product.description,
                                          priceCents: product.priceCents || Math.round((product.price || 0) * 100),
                                          salePriceCents: product.salePriceCents, stock: product.stock || 999,
                                          imageUrl: product.imageUrl || product.image_url, tenantId: listing.tenantId,
                                          availability: product.availability || 'in_stock',
                                          tenantCategory: product.tenantCategory, productCategory: product.category_name,
                                          has_variants: product.has_variants,
                                          payment_gateway_type: paymentGatewayStatus.defaultGatewayType,
                                          featuredType: product.featuredType,
                                          featuredTypes: product.featuredTypes || (product.featuredType ? [product.featuredType] : []),
                                          productType: product.productType || 'physical',
                                        }}
                                        tenantName={listing.businessName} tenantLogo={tenantLogo?.toString() || listing.logoUrl}
                                        defaultGatewayType={paymentGatewayStatus.defaultGatewayType || undefined}
                                        variant="featured" showCategory={true} showDescription={true}
                                      />
                                    ))}
                                  </div>
                                </div>
                              );
                            })}

                            {otherProducts.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Other Products</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                  {otherProducts.map((product: any) => (
                                    <SmartProductCard key={`editorial-${product.id}`} tenantId={listing.tenantId}
                                      product={{
                                        id: product.id, sku: product.sku || product.id,
                                        name: product.name || product.title, title: product.title || product.name,
                                        brand: product.brand, description: product.description,
                                        priceCents: product.priceCents || Math.round((product.price || 0) * 100),
                                        salePriceCents: product.salePriceCents, stock: product.stock || 999,
                                        imageUrl: product.imageUrl || product.image_url, tenantId: listing.tenantId,
                                        availability: product.availability || 'in_stock',
                                        tenantCategory: product.tenantCategory, productCategory: product.category_name,
                                        has_variants: product.has_variants,
                                        payment_gateway_type: paymentGatewayStatus.defaultGatewayType,
                                        featuredType: product.featuredType,
                                        featuredTypes: product.featuredTypes || (product.featuredType ? [product.featuredType] : []),
                                        productType: product.productType || 'physical',
                                      }}
                                      tenantName={listing.businessName} tenantLogo={tenantLogo?.toString() || listing.logoUrl}
                                      defaultGatewayType={paymentGatewayStatus.defaultGatewayType || undefined}
                                      variant="featured" showCategory={true} showDescription={true}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </section>
                </TenantPaymentProvider>
              )}

              {/* Categories */}
              {!showStatusPanel && isStorefrontEnabled && storefrontCategories.categories.length > 0 && (
                <section>
                  <ProductCategoriesCollapsible categories={storefrontCategories.categories} tenantId={listing.tenantId} uncategorizedCount={storefrontCategories.uncategorizedCount} />
                </section>
              )}

              {/* Reviews */}
              {!showStatusPanel && <StoreRatingsSection tenantId={listing.tenantId} showWriteReview={true} />}

              {/* FAQ */}
              {faqFlags?.faq_enabled && faqFlags?.faq_display_storefront_accordion && tenantId && (
                <section className="bg-neutral-50 rounded-xl p-8">
                  <FaqStorefrontDisplay tenantId={tenantId} enabled={faqFlags.faq_enabled && faqFlags.faq_display_storefront_accordion}
                    feedbackEnabled={faqFlags.faq_enabled && faqFlags.faq_display_feedback} defaultExpanded={false} />
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              {/* Contact Card */}
              {!showStatusPanel && (canShowContact || whatsappCtaNumber) && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Contact</h3>
                  {canShowContact ? (
                    <ContactInformationCollapsible tenant={listing} fullAddress={showsLocation ? fullAddress : ''} initialExpanded={true} isRetailStore={true} whatsappCtaNumber={whatsappCtaNumber} onPhoneClick={trackCallClick} />
                  ) : (
                    whatsappCtaNumber && <WhatsAppCtaButton number={whatsappCtaNumber} />
                  )}
                </div>
              )}

              {/* Hours */}
              {!showStatusPanel && showsHours && businessHours && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Hours</h3>
                  <BusinessHoursCollapsible businessHours={businessHours} isRetailStore={true} />
                </div>
              )}

              {/* Map */}
              {!showStatusPanel && showsMap && listing.address && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Location</h3>
                  <GoogleMapEmbed address={listing.address} />
                </div>
              )}

              {/* QR */}
              {canShowQr && (
                <div className="bg-neutral-50 rounded-xl p-6 flex flex-col items-center">
                  <TenantQRCode url={currentUrl} tenantId={listing.tenantId} label="Scan to Share"
                    downloadName={listing.businessName?.toLowerCase().replace(/[^a-z0-9]/g, '-')} size={160} showDownload={true} pageType="directory" isPublic />
                </div>
              )}

              {/* Post-claim report CTA — the prospect's second report surface.
                  Seed listings never reach this layout (directory_seed renders
                  DirectoryNotAvailable), so a seedId here means a claimed seed. */}
              {canShowQr && listing.seedId && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <ClaimedReportCta
                    seedId={listing.seedId}
                    tenantId={listing.tenantId}
                    baseUrl={baseUrl}
                    downloadName={listing.businessName?.toLowerCase().replace(/[^a-z0-9]/g, '-')}
                  />
                </div>
              )}

              {/* Inquiry */}
              {crmFlags?.crm_enabled && crmFlags?.crm_inquiry_directory_enabled && !showStatusPanel && tenantId && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Inquiry</h3>
                  <PublicInquiryForm tenantId={tenantId} tenantName={listing.businessName || listing.name} sourceLabel="Directory" />
                </div>
              )}
            </div>
          </div>
        </div>

        {!showStatusPanel && <RelatedStores currentSlug={slugForRelated} limit={3} title="Similar Stores" />}
        <LastViewed />
        <PoweredByFooter />
      </div>
    </>
  );
}
